import {IActivity} from '../../../../models/activity';
import {IProgram} from '../../../../models/program';
import {ICreateCleanUpForm} from '../index';
import s from '../index.module.scss';

function toDMS(decimal: number, isLat: boolean): string {
    const abs = Math.abs(decimal);
    const deg = Math.floor(abs);
    const minFloat = (abs - deg) * 60;
    const min = Math.floor(minFloat);
    const sec = ((minFloat - min) * 60).toFixed(2);
    const dir = isLat ? (decimal >= 0 ? 'N' : 'S') : decimal >= 0 ? 'E' : 'W';
    return `${deg}°${min}'${sec}"${dir}`;
}

interface Props {
    form: ICreateCleanUpForm;
    programs: IProgram[];
    activities: IActivity[];
    submitError: string | null;
}

export default function Step6Review({form, programs, activities, submitError}: Props) {
    const programName = programs.find((p) => p.id === form.programId)?.name ?? '—';
    const activityName = activities.find((a) => a.id === form.activityId)?.name ?? '—';
    return (
        <div className={s.stepBody}>
            <div className={s.summaryBlock}>
                <div className={s.subSectionTitle}>Summary</div>
                <div className={s.summaryGrid}>
                    <div className={s.summaryItem}>
                        <span className={s.summaryLabel}>Program</span>
                        <span className={s.summaryValue}>{programName}</span>
                    </div>
                    <div className={s.summaryItem}>
                        <span className={s.summaryLabel}>Date</span>
                        <span className={s.summaryValue}>{form.date || '—'}</span>
                    </div>
                    <div className={s.summaryItem}>
                        <span className={s.summaryLabel}>Activity</span>
                        <span className={s.summaryValue}>{activityName}</span>
                    </div>
                    <div className={s.summaryItem}>
                        <span className={s.summaryLabel}>Coordinates</span>
                        <span className={s.summaryValue}>
                            {form.latitude && form.longitude ? (
                                <>
                                    <span className={s.coordNote}>
                                        DD · Lat {form.latitude}, Lng {form.longitude}
                                    </span>
                                    <span className={s.coordNote}>
                                        DMS · {toDMS(parseFloat(form.latitude), true)} (Lat), {toDMS(parseFloat(form.longitude), false)} (Lng)
                                    </span>
                                </>
                            ) : '—'}
                        </span>
                    </div>
                    <div className={s.summaryItem}>
                        <span className={s.summaryLabel}>Participants</span>
                        <span className={s.summaryValue}>{form.num_of_participant || '—'}</span>
                    </div>
                    <div className={s.summaryItem}>
                        <span className={s.summaryLabel}>Waste</span>
                        <span className={s.summaryValue}>
                            {form.no_litter_found
                                ? 'No litter found'
                                : `${Object.values(form.collected_weights).filter((v) => v.wet || v.dry).length} type(s)`}
                        </span>
                    </div>
                    <div className={s.summaryItem}>
                        <span className={s.summaryLabel}>Site Types</span>
                        <span className={s.summaryValue}>
                            {form.site_types.length > 0
                                ? `${form.site_types.length} selected`
                                : '—'}
                        </span>
                    </div>
                    <div className={s.summaryItem}>
                        <span className={s.summaryLabel}>Weather</span>
                        <span className={s.summaryValue}>
                            {form.weather_conditions.filter((wc) => wc.condition_id).length > 0
                                ? `${form.weather_conditions.length} condition(s)`
                                : '—'}
                        </span>
                    </div>
                </div>
            </div>

            {submitError && <div className={s.errorBanner}>{submitError}</div>}

            <p className={s.fieldHint}>
                The clean-up will be saved in multiple requests. If a sub-resource update fails
                after creation, the clean-up will still exist and can be edited later.
            </p>
        </div>
    );
}
